import { useMemo, useState } from 'react';
import Modal from './Modal';
import { useCollection } from '../hooks/useCollection';
import { tenantWhere, resolveActiveTenantId, useActiveTenantId } from '../lib/tenant';
import { availableQualificationArea, templateAreaDefaults, unavailableAreaIds } from '../lib/initiativeAreas';
import { DEFAULT_TASKS, planLeadTimeDays } from '../lib/eventPlan';
import type {
  AppSettings,
  DefaultTaskKind,
  EventTemplate,
  Goal,
  Initiative,
  InitiativeStatus,
  NamedRecord,
  QualificationArea,
} from '../types';

export interface DefaultChoice {
  kind: DefaultTaskKind;
  include: boolean;
  reason: string; // required when include === false
}

export interface InitiativeDraft {
  templateId: string | null;
  name: string;
  /** One entry = a single-district initiative. Several = one initiative per
   *  district, created together (admin only). Locked to one in edit mode. */
  districtIds: string[];
  committee: string;
  strategicGoalId: string | null;
  leadPosition: string | null;
  status: InitiativeStatus;
  notes: string;
  defaults: DefaultChoice[];
  specializedContactAreaIds: string[];
}

export function SpecializedAreaChoices({areas, selected, onChange}: {areas: QualificationArea[]; selected:string[]; onChange:(ids:string[])=>void}) {
  return <fieldset><legend>Specialized contacts</legend>
    <p className="subtitle">Choose applicable Qualifications Areas. Each district will assign its own Person after creation. These are not default Tasks.</p>
    {areas.filter(availableQualificationArea).map(a => <label key={a.id} className="checkbox-row"><input type="checkbox" checked={selected.includes(a.id)} onChange={e=>onChange(e.target.checked?[...selected,a.id]:selected.filter(id=>id!==a.id))}/>{a.name}</label>)}
    {!areas.some(availableQualificationArea) && <p>No available Qualifications Areas.</p>}
  </fieldset>;
}

const STATUSES: InitiativeStatus[] = ['planning', 'active', 'complete', 'archived'];

export default function InitiativeForm({
  initial,
  presetDraft,
  templates,
  goals,
  settings,
  districts,
  committees,
  onClose,
  onSave,
}: {
  initial: Initiative | null;
  /** Prefills a brand-new initiative (e.g. opened from a Goal's "+ Add
   *  Initiative" — locks in its districts/committee/goal link as a starting
   *  point, still fully editable). Ignored when editing an existing one. */
  presetDraft?: Partial<Pick<InitiativeDraft, 'districtIds' | 'committee' | 'strategicGoalId'>>;
  templates: EventTemplate[];
  goals: Goal[];
  settings: AppSettings;
  districts: NamedRecord[];
  committees: NamedRecord[];
  onClose: () => void;
  onSave: (draft: InitiativeDraft, template: EventTemplate | null) => Promise<void>;
}) {
  const editing = !!initial;
  const multiDistrict = !editing && districts.length > 1;
  const tenantId = useActiveTenantId();
  const [openedTenantId] = useState(resolveActiveTenantId);
  const areasQ = useCollection<QualificationArea>('qualificationAreas', [tenantWhere()], !editing);
  const areas = areasQ.data.filter(a => a.tenantId === tenantId);
  const [d, setD] = useState<InitiativeDraft>(() => ({
    templateId: initial?.templateId ?? null,
    name: initial?.name ?? '',
    districtIds: initial
      ? [initial.districtId]
      : (presetDraft?.districtIds ?? districts.map((x) => x.id)),
    committee: initial?.committee ?? presetDraft?.committee ?? committees[0]?.id ?? '',
    strategicGoalId: initial?.strategicGoalId ?? presetDraft?.strategicGoalId ?? null,
    leadPosition: initial?.leadPosition ?? null,
    status: initial?.status ?? 'planning',
    notes: initial?.notes ?? '',
    defaults: DEFAULT_TASKS.map((t) => ({ kind: t.kind, include: true, reason: '' })),
    specializedContactAreaIds: [],
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof InitiativeDraft>(k: K, v: InitiativeDraft[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));

  const template = useMemo(
    () => templates.find((t) => t.id === d.templateId) ?? null,
    [templates, d.templateId],
  );

  const pickTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id) ?? null;
    setD((prev) => ({
      ...prev,
      templateId: t?.id ?? null,
      specializedContactAreaIds: templateAreaDefaults(t),
      name: prev.name || (t ? `${t.name} ${new Date().getFullYear() + 1}` : ''),
      committee:
        t && t.committee && committees.some((c) => c.id === t.committee)
          ? t.committee
          : prev.committee,
    }));
  };

  const setDefault = (kind: DefaultTaskKind, patch: Partial<DefaultChoice>) =>
    setD((prev) => ({
      ...prev,
      defaults: prev.defaults.map((x) => (x.kind === kind ? { ...x, ...patch } : x)),
    }));

  const toggleDistrict = (id: string) =>
    setD((prev) => ({
      ...prev,
      districtIds: prev.districtIds.includes(id)
        ? prev.districtIds.filter((x) => x !== id)
        : [...prev.districtIds, id],
    }));

  const districtGoals = goals.filter(
    (g) =>
      g.status === 'active' &&
      g.committee === d.committee &&
      (g.districtIds ?? []).some((id) => d.districtIds.includes(id)),
  );

  const droppedNeedingReason = d.defaults.some((x) => !x.include && !x.reason.trim());
  const includedCount = d.defaults.filter((x) => x.include).length;
  const unavailable = unavailableAreaIds([...d.specializedContactAreaIds,...templateAreaDefaults(template)],areas);
  const canSave =
    !!d.name.trim() &&
    tenantId === openedTenantId &&
    d.districtIds.length > 0 &&
    !!d.committee &&
    !busy &&
    !droppedNeedingReason && (editing || (!areasQ.loading && !areasQ.error && unavailable.length === 0));

  return (
    <Modal title={editing ? 'Edit Initiative' : 'New Initiative'} onClose={onClose}>
      {!editing && (
        <>
          <label>Start from a template</label>
          <select value={d.templateId ?? ''} onChange={(e) => pickTemplate(e.target.value)}>
            <option value="">— none (build from scratch) —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.source ? ` · ${t.source}` : ''}
              </option>
            ))}
          </select>
          {template && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              {template.steps.length} scheduled steps · earliest{' '}
              {Math.abs(planLeadTimeDays(template))} days before the event
            </div>
          )}
        </>
      )}

      <label>Name</label>
      <input value={d.name} onChange={(e) => set('name', e.target.value)} />

      {multiDistrict ? (
        <>
          <label>Apply to districts</label>
          <div className="form-district-grid">
            {districts.map((x) => (
              <label key={x.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={d.districtIds.includes(x.id)}
                  onChange={() => toggleDistrict(x.id)}
                />
                {x.name}
              </label>
            ))}
          </div>
          {d.districtIds.length > 1 && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              Creates {d.districtIds.length} initiatives — one per district. Each district
              then fills in its own date, location, chair and works its own tasks.
            </div>
          )}
          <label>Committee</label>
          <select value={d.committee} onChange={(e) => set('committee', e.target.value)}>
            {committees.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </>
      ) : (
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label>District</label>
            <select
              value={d.districtIds[0] ?? ''}
              disabled={editing}
              onChange={(e) => set('districtIds', [e.target.value])}
            >
              {districts.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label>Committee</label>
            <select
              value={d.committee}
              disabled={editing}
              onChange={(e) => set('committee', e.target.value)}
            >
              {committees.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <label>Linked goal (optional)</label>
      <select
        value={d.strategicGoalId ?? ''}
        onChange={(e) => set('strategicGoalId', e.target.value || null)}
      >
        <option value="">— none —</option>
        {districtGoals.map((g) => (
          <option key={g.id} value={g.id}>
            {g.title}
          </option>
        ))}
      </select>

      <label>Lead position (optional)</label>
      <select
        value={d.leadPosition ?? ''}
        onChange={(e) => set('leadPosition', e.target.value || null)}
      >
        <option value="">— none —</option>
        {settings.positions.map((p) => (
          <option key={p.name}>{p.name}</option>
        ))}
      </select>

      {editing && (
        <>
          <label>Status</label>
          <select
            value={d.status}
            onChange={(e) => set('status', e.target.value as InitiativeStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </>
      )}

      <label>Notes</label>
      <textarea rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />

      {!editing && (
        <div
          style={{
            marginTop: 12,
            border: '1px solid var(--line)',
            borderRadius: 8,
            padding: 10,
            background: '#fafcff',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            Default tasks ({includedCount} of {DEFAULT_TASKS.length})
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            Every event needs these. Uncheck one only if it truly doesn't apply — you'll
            have to say why, and it's logged.
          </div>
          {DEFAULT_TASKS.map((t) => {
            const choice = d.defaults.find((x) => x.kind === t.kind)!;
            return (
              <div key={t.kind} style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: 0 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={choice.include}
                    onChange={(e) => setDefault(t.kind, { include: e.target.checked })}
                  />
                  <span style={{ fontSize: 13, color: 'var(--ink)' }}>{t.title}</span>
                </label>
                {!choice.include && (
                  <input
                    placeholder="Reason this is being skipped (required)"
                    value={choice.reason}
                    onChange={(e) => setDefault(t.kind, { reason: e.target.value })}
                    style={{ marginTop: 4 }}
                  />
                )}
              </div>
            );
          })}
          {template && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              Plus {template.steps.length} scheduled steps from the template (dates fill in
              once the event date is set).
            </div>
          )}
        </div>
      )}

      {!editing && <>
        {areasQ.loading ? <p role="status">Loading Qualifications Areas…</p> : <SpecializedAreaChoices areas={areas} selected={d.specializedContactAreaIds} onChange={ids=>set('specializedContactAreaIds',ids)} />}
        {areasQ.error && <p role="alert">Unable to load Qualifications Areas. Creation is unavailable until they can be checked.</p>}
        {!areasQ.loading && unavailable.length > 0 && <p role="alert">A selected or template Qualifications Area is unavailable. Review the template or choose “build from scratch”; no substitute Area will be selected.</p>}
      </>}
      {tenantId !== openedTenantId && <p role="alert">Tenant changed. Close and reopen this form.</p>}
      {err && <div className="err">{err}</div>}

      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={!canSave}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave(
                { ...d, name: d.name.trim(), notes: d.notes.trim() },
                editing ? null : template,
              );
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Could not save that Initiative.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {editing
            ? 'Save'
            : d.districtIds.length > 1
              ? `Create ${d.districtIds.length} initiatives`
              : 'Create'}
        </button>
      </div>
    </Modal>
  );
}
